import styled, { css, keyframes } from "styled-components";

import { AIChipButton } from "@similarweb/ai-kit";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { ONBOARDING_AGENT_SECONDARY_ITEM_ANIMATION_MS } from "../onboardingAgentConstants";

const secondaryReveal = keyframes`
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const secondaryRevealAnimation = css`
    animation: ${secondaryReveal} ${ONBOARDING_AGENT_SECONDARY_ITEM_ANIMATION_MS}ms ease-out both;
`;

export const AssistantMessageRoot = styled(Stack)`
    width: 100%;
    gap: 16px;
`;

export const AssistantContentStack = styled(Stack)`
    width: 100%;
    gap: 16px;
    padding-right: 16px;
    box-sizing: border-box;
`;

export const AssistantBodyStack = styled(Stack)`
    width: 100%;
    gap: 0;
`;

export const AssistantBodyParagraph = styled.p`
    margin: 0;
    ${setFontVariant("body1", colorsPalettes.carbon[500])};
`;

export const AssistantMarkdownLink = styled.a`
    color: ${colorsPalettes.blue[400]};
    text-decoration: underline;

    &:hover {
        color: ${colorsPalettes.blue[500]};
    }

    &:focus-visible {
        outline: 2px solid ${colorsPalettes.blue[400]};
        outline-offset: 2px;
    }
`;

export const AssistantMarkdownStrong = styled.strong`
    font-weight: 700;
`;

export const ExploreNextSection = styled(Stack)`
    width: 100%;
    gap: 8px;
    align-items: flex-start;
`;

export const ExploreNextLabel = styled.p`
    margin: 0;
    width: 100%;
    ${setFontVariant("dataTable1", colorsPalettes.carbon[250])};
`;

/** Shared pill styles for quick-action chips (Explore Next uses `AIChipButton` from `@similarweb/ai-kit`). */
const quickActionChipCore = css`
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 8px 12px;
    border-radius: 24px;
    border: 1px solid ${colorsPalettes.carbon[100]};
    background-color: ${colorsPalettes.carbon[0]};
    text-decoration: none;
    ${setFontVariant("body2", colorsPalettes.carbon[400])};
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        background-color 0.15s ease;
    ${secondaryRevealAnimation}

    &:hover {
        border-color: ${colorsPalettes.carbon[200]};
        background-color: ${colorsPalettes.carbon[0]};
    }

    &:focus-visible {
        outline: 2px solid ${colorsPalettes.blue[400]};
        outline-offset: 2px;
    }
`;

export const FollowUpsSection = styled(Stack)`
    width: 100%;
    gap: 8px;
    align-items: stretch;
`;

export const FollowUpsLabel = styled.p`
    margin: 0;
    width: 100%;
    ${setFontVariant("dataTable1", colorsPalettes.carbon[250])};
`;

export const FollowUpsList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: 100%;
`;

export const FollowUpItem = styled.button`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: ${colorsPalettes.carbon[400]};
    ${secondaryRevealAnimation}

    &:hover :last-child {
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 2px solid ${colorsPalettes.blue[400]};
        outline-offset: 2px;
        border-radius: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:disabled:hover :last-child {
        text-decoration: none;
    }
`;

export const FollowUpIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${colorsPalettes.carbon[400]};
`;

export const FollowUpItemText = styled.span`
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    ${setFontVariant("body2", colorsPalettes.carbon[400])};
`;

export const QuickActionsSection = styled(Stack)`
    width: 100%;
    gap: 8px;
    align-items: flex-start;
`;

export const QuickActionsLabel = styled.p`
    margin: 0;
    width: 100%;
    ${setFontVariant("dataTable1", colorsPalettes.carbon[250])};
`;

export const QuickActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    width: 100%;
    align-items: center;
`;

export const QuickActionButton = styled.button`
    ${quickActionChipCore};

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

export const SuggestedActionsSection = styled(Stack)`
    width: 100%;
    gap: 4px;
    align-items: flex-start;
`;

export const SuggestedActionsLabel = styled.p`
    margin: 0;
    width: 100%;
    ${setFontVariant("dataTable1", colorsPalettes.carbon[250])};
`;

export const SuggestedActionsList = styled.ul`
    margin: 0;
    padding-left: 1.25rem;
    list-style-type: disc;
    width: 100%;
    ${setFontVariant("body1", colorsPalettes.carbon[500])};
`;

export const SuggestedActionsItem = styled.li`
    line-height: 24px;
    ${secondaryRevealAnimation}

    &:not(:last-child) {
        margin-bottom: 4px;
    }
`;

export const AssistantFeedbackToolbar = styled(Flex)`
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    ${secondaryRevealAnimation}
`;

export const ReplyFeedbackIconButton = styled(IconButton)`
    svg path {
        fill: ${colorsPalettes.carbon[200]};
    }
`;

export const ReplyFeedbackThumbsDownIconButton = styled(ReplyFeedbackIconButton)<{
    $mirrorIcon: boolean;
}>`
    ${({ $mirrorIcon }) =>
        $mirrorIcon &&
        css`
            svg {
                transform: scaleX(-1);
            }
        `}
`;

export const ReplyFeedbackCheckedIconContainer = styled(Flex)`
    align-items: center;
    justify-content: center;
    padding: 4px;
`;

export const OnboardingAIChip = styled(AIChipButton)`
    [class*="_ai_chip_content"] {
        display: flex;
        flex-direction: row-reverse;
        gap: 4px;
    }
`;
